import React, { useState, useEffect } from 'react';
import { AppBar, Tab, Box, MenuItem, Menu } from '@mui/material';
import DocumentContainer from '../DocumentContainer';
import TabContainer from 'global/components/TabContainer';
import { StyledRoot, StyledTabs, StyledContent } from './DocumentTabs.styles';
import { getLabel, getIcon, FILEADD, VIDEOADD, ADDTAB, INDOC } from '../definitions';
import { getUrlParams } from 'global/utils';
import { get, first } from 'global/lib/lodash';
import { withRouter } from 'react-router-dom';
import { useEdit } from '../../../global/hooks/useEdit';
import CopyrightNotice from 'global/components/PageFooter';

export default withRouter((props) => {
  const { documents, userCanEdit } = props;
  const [currentTab, setCurrentTab] = useState('');
  const { editOn } = useEdit();

  const hasFilePerms = userCanEdit;

  useEffect(() => {
    const { activeTab } = getUrlParams(props.location.search);
    const docs = documents || [];
    const isAdd = ADDTAB.children.find((f) => f.id == activeTab);

    if (activeTab && isAdd) {
      const hasFile = docs.find((f) => f.documentableType == isAdd.documentableType);
      setCurrentTab(hasFile ? hasFile.id : activeTab);
      return;
    }

    // Match by id (tab links) or documentableType (doctype chips). If Use Location
    // filtered the target out, fall back to primary / first remaining tab.
    const match = activeTab && docs.find((d) => d.id == activeTab || d.documentableType === activeTab);
    const primary = docs.find((d) => !!d.primaryDocument);
    setCurrentTab(get(match || primary, 'id', get(docs, '0.id')));
  }, [documents]);

  const changeLocation = (d) => {
    const { replace } = props.history;
    const { pathname, search } = props.location;

    let activeTab = d.id;

    const isAdd = ADDTAB.children.find((f) => f.id == activeTab);
    if (isAdd) {
      const hasFile = (documents || []).find((f) => f.documentableType == isAdd.documentableType);
      if (hasFile && get(isAdd, 'shouldCheckForMatches', true)) {
        activeTab = hasFile.id;
      }
    }

    const obj = {
      ...getUrlParams(search),
      activeTab,
    };

    const str = new URLSearchParams(obj).toString();

    if (!props.isModal) {
      replace(`${pathname}?${str}`);
    }

    setCurrentTab(activeTab);
  };

  const getTabs = () => {
    const nonFiles = (documents || []).filter((d) => d.documentableType !== FILEADD.documentableType && d.documentableType !== VIDEOADD.documentableType);
    const hasVideo = (documents || []).find((d) => d.documentableType === VIDEOADD.documentableType);
    const hasFile = (documents || []).find((d) => d.documentableType === FILEADD.documentableType);

    //only want one files tab, so we are grabbing the first, or a "create new" tab
    const files = [...(hasFile ? [hasFile] : hasFilePerms ? [] : []), ...(hasVideo ? [hasVideo] : hasFilePerms ? [] : [])];
    return [...nonFiles, ...files, hasFilePerms && editOn && ADDTAB].filter((f) => !!f);
  };

  const getDoc = () => {
    const _doc = (documents || []).find((d) => d.id === currentTab);

    const editTab = ADDTAB.children.find((c) => c.id === currentTab);

    if (editTab) {
      return [
        {
          ...editTab,
          classification: get(first(documents), 'classification'),
          classificationId: get(first(documents), 'classificationId'),
          editionId: get(first(documents), 'editionId'),
        },
      ];
    }

    if (!props.isModal && _doc && _doc.documentableType === FILEADD.documentableType) {
      return (documents || [])
        .filter((d) => d.documentableType === FILEADD.documentableType)
        .map((d) => ({
          ...FILEADD,
          ...d,
        }));
    } else if (!props.isModal && _doc && _doc.documentableType === VIDEOADD.documentableType) {
      return (documents || [])
        .filter((d) => d.documentableType === VIDEOADD.documentableType)
        .map((d) => ({
          ...VIDEOADD,
          ...d,
        }));
    } else if (!props.isModal && _doc && _doc.documentableType === INDOC.documentableType) {
      return (documents || [])
        .filter((d) => d.documentableType === INDOC.documentableType)
        .map((d) => ({
          ...INDOC,
          ...d,
        }));
    }

    return _doc;
  };

  const update = async () => {
    await Promise.all([await props.update(), await get(window, 'DOC_LIST_EDITION.update', () => 0)()]);
  };

  const doc = getDoc();

  const isInteractiveDoc = (() => {
    if (Array.isArray(doc)) {
      return get(doc, '0.documentableType') === INDOC.documentableType;
    }
    return get(doc, 'documentableType') === INDOC.documentableType;
  })();

  useEffect(() => {
    if (!!doc) {
      props.onDoc && props.onDoc(doc);
    }
  }, [doc]);

  return (
    <StyledRoot isModal={props.isModal} fillRemaining={isInteractiveDoc}>
      <Box sx={{
        displayPrint: "none"
      }}>
        <AppBar position="static">
          <StyledTabs value={currentTab} size="small" variant="scrollable">
            {getTabs().map((d) => (
              <DocTab {...d} onClick={changeLocation} currentTab={currentTab} />
            ))}
          </StyledTabs>
        </AppBar>
      </Box>
      <TabContainer fillRemaining={isInteractiveDoc}>
        <StyledContent fillRemaining={isInteractiveDoc}>
          <DocumentContainer {...props} update={update} doc={doc} />
          {!props.isModal && !isInteractiveDoc && (
            <CopyrightNotice
              ownerRealmId={get(props, 'edition.ownerRealmId', get(props, 'edition.0.ownerRealmId'))}
            />
          )}
        </StyledContent>
      </TabContainer>
    </StyledRoot>
  );
});

export const DocTab = (props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { documentableType, id, currentTab } = props;

  const onClick = (e) => {
    if (id !== ADDTAB.id) {
      return props.onClick(props);
    }

    setAnchorEl(e.currentTarget);
  };

  const onClickChild = (e, child) => {
    setAnchorEl(null);
    props.onClick(child);
  };

  return (
    <>
      <Tab sx={{ flexDirection: 'row' }} icon={getIcon(documentableType, { style: { margin: '0 5px 0 0' } })} selected={currentTab == id} label={getLabel(documentableType, props)} value={id} onClick={onClick} aria-controls="customized-menu" />
      {!!props.children && (
        <Menu
          id="customized-menu"
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          onClose={() => setAnchorEl(null)}
        >
          {props.children.map((child) => (
            <MenuItem onClick={(e) => onClickChild(e, child)}>{getLabel(child.documentableType, child)}</MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};
